import {
  type ResponseData,
  useInfiniteApiQuery,
  type RequestQuery,
  useTeam,
} from '@vrplatform/data';
import { useAuditEffectsTableFilter } from './useAuditEffectsTableFilter';
import {
  useAuditEffectsModalFilter,
  useAuditEffectsModalWhere,
} from './useAuditEffectsModal';

const LIMIT = 50;

type TableFilter = ReturnType<typeof useAuditEffectsTableFilter>['filter'];
type ModalWhere = ReturnType<typeof useAuditEffectsModalWhere>;

type AuditEffectsRequest = ResponseData<'get:/audit/effects'>;

type AuditEffectsApiFilter = RequestQuery<'get:/audit/effects'>;

export type AuditEffectRow = AuditEffectsRequest['data'][number];

export const getAuditEffectsApiQueryFilter = ({
  limit,
  modalFilter,
  modalWhere,
  page,
  tableFilter,
}: {
  page: number | undefined;
  limit: number | undefined;
  tableFilter: TableFilter;
  modalFilter: TableFilter;
  modalWhere: ModalWhere;
}): AuditEffectsApiFilter => {
  const filter = modalWhere ? modalFilter : tableFilter;

  return {
    limit,
    page,
    actionId: modalWhere?.actionId,
    entityId: modalWhere?.entityId,
    status: filter.status ?? undefined,
    effectType: filter.effectType ?? undefined,
    entityType: filter.entityType ?? undefined,
  };
};

export function useAuditEffectsTableQuery({ skip }: { skip: boolean }) {
  'use memo';

  const { filter: tableFilter } = useAuditEffectsTableFilter();
  const { filter: modalFilter } = useAuditEffectsModalFilter();
  const modalWhere = useAuditEffectsModalWhere();

  const [{ id: teamId }] = useTeam();

  return useInfiniteApiQuery(
    async (api, signal, args, pageParam) => {
      const result = await api.GET('/audit/effects', {
        params: {
          query: getAuditEffectsApiQueryFilter({
            page: pageParam,
            limit: LIMIT,
            tableFilter: args.tableFilter,
            modalFilter: args.modalFilter,
            modalWhere: args.modalWhere,
          }),
        },
        signal,
      });

      if (result.error) throw result.error;

      const data = result.data;

      return {
        list: data.data,
        aggregate: data.pagination.total,
        pagination: data.pagination,
      };
    },
    {
      initialPageParam: 1,
      skip,
      queryGroup: 'connections',
      uniqueQueryKey: 'audit-effects-table',
      variables: {
        teamId,
        tableFilter,
        modalFilter,
        modalWhere,
      },
      getNextPageParam: (lastPage) => {
        return lastPage.pagination?.nextPage ?? null; // return null if no more pages
      },
      hideNotification: true,
    }
  );
}
